import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, mediaUrl } from "../../lib/api";
import { AdminHeader, Modal, ConfirmDelete, EditBtn, AddBtn, Field } from "../../components/admin/AdminUI";

const empty = {
  name: "",
  designation: "Member",
  committee: "",
  gender: "male",
  photo: "",
  display_order: 0,
  active: true,
};

export default function CommitteeAdmin() {
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = () => {
    Promise.all([api.get("/admin/committee"), api.get("/admin/committee/sections")]).then(([members, sectionRes]) => {
      setItems(members.data || []);
      setSections(sectionRes.data || []);
    });
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    return sections.map((section) => ({
      ...section,
      members: items.filter((item) => item.committee === section.name),
    }));
  }, [items, sections]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, committee: sections[0]?.name || "Office Bearers" });
    setOpen(true);
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/committee/${editing}`, form);
      else await api.post("/admin/committee", form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const edit = (c) => {
    setEditing(c.id);
    setForm({ ...empty, ...c });
    setOpen(true);
  };

  const del = async (id) => { await api.delete(`/admin/committee/${id}`); load(); toast.success("Deleted"); };

  return (
    <div>
      <AdminHeader title="Organization Structure" subtitle="Manage official office bearers and committees" actions={<AddBtn onClick={openNew} label="Add Member" />} />

      <div className="space-y-8">
        {grouped.map((section) => (
          <div key={section.id} className="card-elev p-5">
            <div className="flex items-center justify-between gap-4 mb-5">
              <div>
                <h2 className="font-serif text-xl font-bold text-brand-text">{section.name}</h2>
                <div className="text-xs text-brand-muted mt-1">{section.members.length} members</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {section.members.map((c) => (
                <div key={c.id} className={`rounded-xl border bg-white p-4 ${c.active ? "border-brand-border" : "border-slate-200 opacity-70"}`}>
                  <div className="flex gap-3">
                    <img src={mediaUrl(c.photo)} className="w-16 h-16 rounded-full object-cover ring-2 ring-brand-primary-light shrink-0" alt={c.name} />
                    <div className="min-w-0 flex-1">
                      <div className="font-serif font-bold text-brand-text truncate">{c.name}</div>
                      <div className="text-xs text-brand-primary font-semibold uppercase tracking-wider mt-0.5">{c.designation}</div>
                      <div className="text-xs text-brand-muted mt-1">Order: {c.display_order}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-brand-border pt-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest ${c.active ? "bg-brand-accent/10 text-brand-accent" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Active" : "Inactive"}</span>
                    <div className="flex items-center gap-1"><EditBtn onClick={() => edit(c)} /><ConfirmDelete onConfirm={() => del(c.id)} /></div>
                  </div>
                </div>
              ))}
              {section.members.length === 0 && <div className="text-sm text-brand-muted py-6">No members in this section.</div>}
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Organization Member" : "Add Organization Member"} size="lg">
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name *"><input required value={form.name} onChange={upd("name")} className="form-input" /></Field>
          <Field label="Designation *"><input required value={form.designation} onChange={upd("designation")} className="form-input" /></Field>
          <Field label="Committee *">
            <select required value={form.committee} onChange={upd("committee")} className="form-input">
              {sections.map((section) => <option key={section.id} value={section.name}>{section.name}</option>)}
            </select>
          </Field>
          <Field label="Gender">
            <select value={form.gender} onChange={upd("gender")} className="form-input">
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </Field>
          <Field label="Photo Path">
            <input value={form.photo} onChange={upd("photo")} className="form-input" placeholder="/uploads/images/avatar-male.png" />
          </Field>
          <Field label="Display Order"><input type="number" value={form.display_order} onChange={upd("display_order")} className="form-input" /></Field>
          <Field label="Status">
            <select value={form.active ? "active" : "inactive"} onChange={(e) => setForm({ ...form, active: e.target.value === "active" })} className="form-input">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </Field>
          <div className="flex items-end">
            <Link to="/admin/media" className="btn-ghost w-full justify-center">Open Media Library</Link>
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
            <button className="btn-primary">{editing ? "Update" : "Create"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
